<script lang="ts" setup>
import ArticleCard from "@/components/ArticleCard.vue";
import BaseCard from "@/components/BaseCard.vue";
import BaseComment from "@/components/BaseComment.vue";
import BaseContent from "@/components/BaseContent.vue";
import BaseLink from "@/components/BaseLink.vue";
import BasePagination from "@/components/BasePagination.vue";
import BaseTab from "@/components/BaseTab.vue";
import BaseTabList from "@/components/BaseTabList.vue";
import BaseTabPanel from "@/components/BaseTabPanel.vue";
import DokkanCharacterList from "@/components/DokkanCharacterList.vue";
import TheLayout from "@/components/TheLayout.vue";
import TheSignInDialog from "@/components/TheSignInDialog.vue";
import { PaginatedCommentData } from "@/types";
import { Character } from "@/types/character";
import { TabGroup, TabPanels } from "@headlessui/vue";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { ref } from "vue";
import { route } from "ziggy-js";

const breakpoints = useBreakpoints(breakpointsTailwind);

defineProps<{
  characters: Character[];
  news_articles: object;
  comments: PaginatedCommentData;
}>();

const signInDialogOpen = ref(false);
</script>

<template>
  <TheLayout
    :show-header-ad="breakpoints.greaterOrEqual('sm').value"
    :show-headline="false"
    has-page-heading
    heading="Home"
    title="Dragon Ball Z Dokkan Battle Database"
  >
    <div class="flex flex-col gap-4 lg:flex-row">
      <div
        class="order-1 flex shrink-0 flex-col gap-4 lg:order-2 lg:w-5/12 xl:w-1/3 2xl:w-1/4"
      >
        <section>
          <BaseCard
            :footer="{
              href: route('characters.index'),
            }"
          >
            <template #heading>Latest Characters</template>

            <template #footer>Show All Characters</template>

            <DokkanCharacterList :characters="characters" scale="sm" />
          </BaseCard>
        </section>
      </div>

      <div class="order-2 flex w-full flex-col gap-4 lg:order-1">
        <section class="flex w-full flex-1 flex-col gap-4">
          <TabGroup>
            <BaseTabList>
              <BaseTab>News &amp; Updates</BaseTab>
              <BaseTab>Latest Comments</BaseTab>
            </BaseTabList>

            <TabPanels>
              <BaseTabPanel>
                <ArticleCard
                  v-for="article in news_articles.data"
                  :key="article.id"
                  :article="article"
                />

                <BasePagination
                  v-if="news_articles.meta.total > news_articles.meta.per_page"
                  :meta="news_articles.meta"
                  name="news"
                />
              </BaseTabPanel>

              <BaseTabPanel>
                <BaseCard class="!gap-0 divide-y divide-green-900/75 !p-0">
                  <template #heading>Latest Comments</template>

                  <BaseContent
                    v-for="comment in comments.data"
                    v-if="comments.data.length"
                    :key="comment.id"
                    class="p-4"
                  >
                    <template #header>{{ comment.commentable_name }}</template>
                    <BaseComment :comment="comment" :show-replies="false" />

                    <div class="place-self-end">
                      <BaseLink
                        :href="`${comment.url}#comment-${comment.id}`"
                        class="text-xs"
                        >View Comment</BaseLink
                      >
                    </div>
                  </BaseContent>

                  <div v-else>
                    <p>No comments were posted yet.</p>
                  </div>
                </BaseCard>

                <BasePagination
                  v-if="comments.meta.total > comments.meta.per_page"
                  :meta="comments.meta"
                  name="comments"
                />
              </BaseTabPanel>
            </TabPanels>
          </TabGroup>
        </section>
      </div>
    </div>
  </TheLayout>

  <TheSignInDialog v-model:open="signInDialogOpen" />
</template>

<style scoped></style>
